import React, { useEffect, useRef } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import TableOfContents from '@/components/resources/TableOfContents';
import EmailCapture from '@/components/resources/EmailCapture';

const TITLE = "A Missions Pastor's Checklist: Vetting Fundraising Platforms for Your Sent Missionaries";
const DESCRIPTION =
    "Twelve questions every missions pastor or committee should ask before recommending a support-raising platform to a sent missionary — fees, payout speed, data ownership, and the questions vendors hope you skip.";

const JSON_LD = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'Article',
            headline: TITLE,
            description: DESCRIPTION,
            image: 'https://devotionfund.com/resources/cover19.jpg',
            datePublished: '2026-07-28',
            dateModified: '2026-07-28',
            author: { '@type': 'Organization', name: 'Devotion Fund', url: 'https://devotionfund.com' },
            publisher: {
                '@type': 'Organization',
                name: 'Devotion Fund',
                logo: { '@type': 'ImageObject', url: 'https://devotionfund.com/logo.png' },
            },
            mainEntityOfPage: {
                '@id': 'https://devotionfund.com/resources/missions-pastor-checklist-vetting-fundraising-platforms',
                '@type': 'WebPage',
            },
        },
        {
            '@type': 'FAQPage',
            mainEntity: [
                {
                    '@type': 'Question',
                    name: 'What should a missions committee ask about fees before approving a platform?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Ask for the all-in number, not the headline rate: the platform fee plus card/ACH processing plus any monthly or per-missionary subscription, and whether the missionary or the donor pays it. Also ask what happens on a failed or disputed recurring payment, since that cost is often left out of the pricing page entirely.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Should a sending church require missionaries to use one specific platform?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Most sending churches are better served by a short vetted list than a single mandate, since missionaries in different countries and denominational contexts have different needs around anonymity, tax receipting, and currency. A shared checklist keeps the vetting consistent even when the final choice varies.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'How fast should donor payments reach a missionary?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Ask for the actual payout schedule in writing, not a marketing phrase like 'fast payouts.' Weekly or faster payout cycles matter most for missionaries relying on support to cover monthly field expenses, and a platform that only pays out monthly can create real cash-flow strain.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'What happens to a missionary\'s donor list if they leave a platform?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Confirm in writing whether the missionary can export their donor contact list and giving history if they switch platforms or transition off the field. A platform that treats the donor relationship as its own asset, rather than the missionary's, leaves a sent worker starting over if the relationship with the vendor ever ends.",
                    },
                },
            ],
        },
    ],
};

export default function MissionsPastorChecklistVettingFundraisingPlatforms() {
    usePageMeta(TITLE, DESCRIPTION, { path: '/resources/missions-pastor-checklist-vetting-fundraising-platforms' });

    const articleRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-missions-pastor-checklist-jsonld', 'true');
        el.textContent = JSON.stringify(JSON_LD);
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <HomeHeader />
            <div className="w-full h-64 md:h-80 bg-gray-100 overflow-hidden">
                <img src="/resources/cover19.jpg" alt="A missions pastor reviewing paperwork with a sent missionary before a trip" className="w-full h-full object-cover" />
            </div>
            <div className="max-w-3xl mx-auto px-4 py-12 w-full">
                <h1 className="text-3xl font-bold mb-6">{TITLE}</h1>
                <TableOfContents targetRef={articleRef} />
                <div className="prose max-w-none" ref={articleRef}>
                    <p>When a missions pastor or committee tells a sent missionary "use whatever platform you like," the church usually inherits the consequences anyway — a missionary who runs out of runway because payouts were slow, a donor who calls the church office confused about a fee on their statement, or a support base the missionary can't take with them when they transition off the field. A short, consistent vetting checklist protects both the missionary and the church's reputation with donors, without requiring anyone to become a payments expert.</p>

                    <h2>1. Ask for the all-in fee, not the headline rate</h2>

                    <p>Every platform advertises a number that looks good on a landing page. The real cost is the platform fee plus card or ACH processing plus any monthly subscription, expressed as a single percentage of a typical monthly gift. Ask the vendor to write that number down for a $75 recurring gift specifically, since flat-rate math looks different at small gift sizes than the marketing examples usually show.</p>

                    <h2>2. Find out who actually pays the fee</h2>

                    <p>Some platforms deduct fees from the missionary's payout; others offer donors the option to cover the fee at checkout. Ask what percentage of donors actually opt in to covering fees in practice, not just whether the option exists — a feature that's technically available but rarely used doesn't change the missionary's real take-home.</p>

                    <h2>3. Confirm the payout schedule in writing</h2>

                    <p>"Fast payouts" means nothing without a number attached. Ask specifically how many business days after a gift the money lands in the missionary's account, and whether that schedule changes for new accounts, international bank details, or larger one-time gifts. A missionary covering monthly field expenses needs a predictable cycle more than an occasional fast one.</p>

                    <h2>4. Ask what happens on a failed recurring payment</h2>

                    <p>Recurring gifts fail — expired cards, insufficient funds, bank holds — more often than committees expect, and a platform's recovery process matters as much as its sign-up flow. Ask whether the platform automatically retries and notifies the donor, or whether the missionary has to notice the gap and chase it down manually.</p>

                    <h2>5. Confirm the missionary owns the donor relationship</h2>

                    <p>Ask directly, in writing: can the missionary export their full donor list and giving history if they ever switch platforms or come off the field? A platform that treats donor data as proprietary leaves a sent worker starting from zero at the exact moment — a transition, a furlough, a platform shutting down — when they can least afford to.</p>

                    <h2>6. Check how transparent the platform is with donors</h2>

                    <p>Donors increasingly ask what a platform takes before they give. A platform with a public, plain-language fee page that a donor can read in under a minute reflects well on the missionary and the sending church; a fee structure buried in a terms-of-service document invites the awkward follow-up call to the church office.</p>

                    <h2>7. Ask about tax receipting, plainly</h2>

                    <p>Confirm exactly when a gift is tax-deductible on that platform and when it isn't — direct personal support and gifts through a registered giving structure are treated very differently. See <a href="/resources/is-missionary-support-tax-deductible">is missionary support tax-deductible</a> for the plain-English breakdown to walk a missionary through before they commit to a platform.</p>

                    <h2>8. Ask how recurring, not one-time, giving is handled</h2>

                    <p>A platform built primarily for one-time crowdfunding campaigns often treats monthly giving as an afterthought — clunkier update tools, weaker retry logic on failed cards, fewer ways for a donor to manage their own subscription. Since recurring support is what actually sustains a missionary long-term, ask to see the recurring-donor experience specifically, not just the one-time checkout demo.</p>

                    <h2>9. Ask what the missionary is required to post publicly</h2>

                    <p>Some platforms require a public page with a photo, name, and country of service by default — a real problem for missionaries serving in restricted-access contexts. Confirm whether privacy settings exist and are genuinely usable, not just a checkbox buried in account settings that most missionaries never find.</p>

                    <h2>10. Ask how customer support actually works</h2>

                    <p>When a donor's card fails or a payout doesn't arrive, does the missionary reach a real person, or a support ticket queue with a multi-day response time? Ask for a real support-response benchmark, and if possible, ask a missionary already using the platform how a support issue was actually resolved.</p>

                    <h2>11. Ask what the platform costs the church, not just the missionary</h2>

                    <p>Some platforms bundle church-level dashboards, reporting for missions committees, or bulk-onboarding for multiple sent workers — sometimes as a paid add-on, sometimes included. If your church sends more than one or two missionaries, ask specifically whether committee-level visibility exists and what it costs.</p>

                    <h2>12. Ask what happens if the missionary needs to leave the platform</h2>

                    <p>Confirm the offboarding process before anyone signs up, not after a problem arises: how long does a final payout take, is there a fee to close an account, and how quickly can a donor list be exported. A platform confident in its own value has a fast, well-documented answer to this question.</p>

                    <h2>Frequently asked questions</h2>

                    <p><strong>What should a missions committee ask about fees before approving a platform?</strong><br />
                    Ask for the all-in number — platform fee plus processing plus any subscription — for a real $75 recurring gift, and who pays it.</p>

                    <p><strong>Should a sending church require missionaries to use one specific platform?</strong><br />
                    A short vetted list usually serves missionaries better than a single mandate, since needs vary by field and context.</p>

                    <p><strong>How fast should donor payments reach a missionary?</strong><br />
                    Ask for the actual payout schedule in business days — weekly or faster matters most for covering monthly expenses.</p>

                    <p><strong>What happens to a missionary's donor list if they leave a platform?</strong><br />
                    Confirm in writing that the missionary can export their full donor contact list and giving history at any time.</p>

                    <h2>Next reads on Devotion Fund</h2>
                    <ul>
                        <li><a href="/resources/how-to-ask-your-church-to-support-you-as-a-missionary">How to Ask Your Church to Support You as a Missionary (Step-by-Step)</a></li>
                        <li><a href="/resources/is-missionary-support-tax-deductible">Is Missionary Support Tax-Deductible? A Clear Guide for Donors and Missionaries</a></li>
                        <li><a href="/resources/true-cost-missionary-fundraising-platforms-2026">The True Cost of Every Missionary Fundraising Platform in 2026</a></li>
                    </ul>

                    <h2>About the author</h2>
                    <p>We're the team behind <a href="/">Devotion Fund</a>. We put this checklist together after hearing the same questions from missions pastors trying to vet a platform on behalf of a missionary they're sending, without a background in payments.</p>
                    <p><small><em>Last updated: 2026-07-28.</em></small></p>
                </div>
                <div className="mt-8">
                    <EmailCapture page="/resources/missions-pastor-checklist-vetting-fundraising-platforms" />
                </div>
            </div>
            <Footer />
        </div>
    );
}
